import React, { ReactElement } from 'react';
import { observer } from 'mobx-react-lite';
import { Navigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useDataStore, useOnboardingStore } from '../../providers/StoresProvider';
import { AppConfig } from '../../../core/AppConfig';
import { AppRoutes } from '../../../core/routes';
import { OnboardingRoutes } from '../../../core/routes/OnboardingRoutes';
import { buildUrlWithParams } from '../../utils/WebHelper';
import {
  getOnboardingResumeRoute,
  isOnboardingComplete,
  redirectToPhoneVerification,
} from '../../utils/OnboardingHelper';
import { handleErrorLogging } from '../../../core/utils/CoreHelper';

const OnboardingRouter = observer(({ children }: { children: ReactElement }) => {
  const dataStore = useDataStore();
  const onboardingStore = useOnboardingStore();
  const { pathname, search } = useLocation();
  const { userState } = dataStore;
  const { state: onboardingState } = onboardingStore;
  const { Settings } = AppConfig;
  const { cookie } = Settings.Authentication;
  const { name: authCookieName } = cookie;
  const authCookiePresent = Cookies.get(authCookieName);

  const isGetStartedPage = pathname === OnboardingRoutes.GetStarted || pathname === OnboardingRoutes.Onboarding;

  if (!authCookiePresent) {
    if (!isGetStartedPage) {
      return <Navigate to={buildUrlWithParams(OnboardingRoutes.GetStarted)} replace />;
    }
    return children;
  }

  if (authCookiePresent && !userState.user && !userState.fetchingUser) {
    dataStore.fetchUser().catch(e => handleErrorLogging(e));
    return null;
  }

  if (userState.fetchingUser && !userState.user) {
    return null;
  }

  if (userState.user?.is_staff) {
    if (isGetStartedPage) {
      return <Navigate to={buildUrlWithParams(AppRoutes.Overview)} replace />;
    }
    return children;
  }

  if (userState.user && !onboardingState.progressFetched && !onboardingState.isFetchingProgress) {
    onboardingStore.fetchOnboardingProgress().catch(e => handleErrorLogging(e));
    return null;
  }

  if (onboardingState.isFetchingProgress) {
    return null;
  }

  if (onboardingState.progressFetched && onboardingState.progress && userState.user) {
    const result = getOnboardingResumeRoute(onboardingState.progress, userState.user);

    if (result?.type === 'phone_verification' && isGetStartedPage) {
      return redirectToPhoneVerification(pathname, search);
    }

    const completed = isOnboardingComplete(onboardingState.progress);

    if (completed && isGetStartedPage) {
      return <Navigate to={buildUrlWithParams(AppRoutes.Overview)} replace />;
    }

    if (result?.type === 'route' && isGetStartedPage) {
      return <Navigate to={buildUrlWithParams(result.path)} replace />;
    }
  } else if (onboardingState.progressFetched && !onboardingState.progress && userState.user) {
    if (!userState.user.email_verified && isGetStartedPage) {
      return <Navigate to={buildUrlWithParams(OnboardingRoutes.VerifyEmail)} replace />;
    }
    if (!userState.user.phone_number_verified && isGetStartedPage) {
      return redirectToPhoneVerification(pathname, search);
    }
  }

  return children;
});

export default OnboardingRouter;
